/**
 * LiveDebugPanel – always-on right sidebar that interleaves a thread's chat messages with its
 * tool audit rows, oldest first (newest at the bottom, like the chat). The newest entry is open; older ones fold away as new ones
 * arrive unless the user opened or closed them by hand.
 *
 * It mounts into the app shell's `#theo-live-debug-slot` so it pushes the page over instead of
 * covering the chat. Without that slot (embeds) it falls back to a fixed overlay.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import CloseIcon from "@mui/icons-material/Close";
import RefreshIcon from "@mui/icons-material/Refresh";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { useSelector } from "react-redux";
import {
  chatApiInstance as chatApi,
  type ThreadAutomation,
  type ThreadResolverState,
} from "../apis/chatApi";
import {
  getThreadUsage,
  type ThreadUsage,
  type ThreadTurnUsage,
} from "../observer/apis/observerApi";
import {
  formatDuration,
  formatParamsAsKeyValue,
  getActionTypeLabel,
  getFullOutputString,
  isErrorRow,
  scrollSx,
  type ToolAuditLogRow,
} from "../utils/toolAuditLogUtils";

export const LIVE_DEBUG_SLOT_ID = "theo-live-debug-slot";
const PANEL_WIDTH = 440;
const POLL_WHILE_THINKING_MS = 2500;
const POLL_IDLE_MS = 15000;
// LangSmith ingests runs in batches, so a turn's totals settle a few seconds after it ends.
const LANGSMITH_SETTLE_MS = 8000;

type AuditRow = ToolAuditLogRow & {
  id: number | string;
  audit_event_type?: "playbook_context";
  audit_turn_id?: string | null;
  call_id?: string | null;
  is_undoable?: boolean;
  description?: string;
};

/** A progress line Cortex streamed mid-turn ("Searching users…"), stamped when it arrived. */
export type ProgressUpdate = { key: string; at: number; text: string };

export type LiveDebugEntry =
  | { kind: "message"; key: string; at: number; message: any }
  | { kind: "tool"; key: string; at: number; row: AuditRow }
  | { kind: "turn"; key: string; at: number; turn: ThreadTurnUsage }
  | { kind: "progress"; key: string; at: number; text: string };

function messageTimeMs(message: any): number {
  const created = message?.created_at;
  if (created && typeof created === "object" && created.seconds != null) {
    return Number(created.seconds) * 1000 + Math.floor(Number(created.nanos || 0) / 1e6);
  }
  if (typeof created === "string" || typeof created === "number") {
    const parsed = new Date(created).getTime();
    if (!Number.isNaN(parsed)) return parsed;
  }
  // Pending or streaming messages have no server time yet; they are the newest thing on screen.
  return Number.MAX_SAFE_INTEGER;
}

function auditTimeMs(row: AuditRow): number {
  return isoTimeMs(row.start_timestamp || row.end_timestamp);
}

export function formatTokens(n: number | null | undefined): string {
  if (n == null) return "—";
  const v = Number(n);
  if (v >= 1_000_000) return `${(v / 1_000_000).toFixed(1)}M`;
  if (v >= 1000) return `${(v / 1000).toFixed(1)}k`;
  return String(v);
}

export function formatCost(n: number | null | undefined): string {
  if (n == null) return "—";
  const v = Number(n);
  if (v === 0) return "$0";
  return v < 0.01 ? `$${v.toFixed(4)}` : `$${v.toFixed(2)}`;
}

const AUTOMATION_LABEL: Record<number, string> = { 0: "plain chat", 1: "suggestions", 3: "auto resolver" };
const INTERACTION_LABEL: Record<number, string> = { 0: "no customer contact", 1: "investigative", 2: "full interaction" };

function isoTimeMs(iso: string | null | undefined): number {
  const parsed = iso ? new Date(iso).getTime() : NaN;
  return Number.isNaN(parsed) ? 0 : parsed;
}

/** Messages, audit rows, LLM turns and progress updates on one timeline, oldest first. */
export function buildLiveDebugTimeline(
  messages: any[],
  auditRows: AuditRow[],
  turns: ThreadTurnUsage[] = [],
  progress: ProgressUpdate[] = []
): LiveDebugEntry[] {
  const entries: LiveDebugEntry[] = [];
  // Turns go first so a turn marker sorts ahead of anything that started in the same instant.
  turns.forEach((turn) => {
    entries.push({ kind: "turn", key: `turn-${turn.turn_id}`, at: isoTimeMs(turn.start_time), turn });
  });
  messages.forEach((message, index) => {
    entries.push({
      kind: "message",
      key: `msg-${message?.message_id || message?.requestId || `idx-${index}`}`,
      at: messageTimeMs(message),
      message,
    });
  });
  auditRows.forEach((row) => {
    entries.push({ kind: "tool", key: `tool-${row.id}`, at: auditTimeMs(row), row });
  });
  progress.forEach((update) => {
    entries.push({ kind: "progress", key: update.key, at: update.at, text: update.text });
  });
  // Stable on ties: within the same instant keep source order.
  return entries
    .map((entry, index) => ({ entry, index }))
    .sort((a, b) => a.entry.at - b.entry.at || a.index - b.index)
    .map(({ entry }) => entry);
}

function formatClock(ms: number): string {
  if (!Number.isFinite(ms) || ms <= 0 || ms === Number.MAX_SAFE_INTEGER) return "now";
  return new Date(ms).toLocaleTimeString("en-US", {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
}

const COLORS = {
  bg: "#16181d",
  card: "#1e2128",
  border: "#2c313a",
  text: "#e6edf3",
  muted: "#8b949e",
  tool: "#d2a8ff",
  playbook: "#79c0ff",
  user: "#7ee787",
  theo: "#ffa657",
  error: "#ff7b72",
  turn: "#e3b341",
};

const preSx = {
  m: 0,
  mt: 0.5,
  p: 1,
  fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
  fontSize: 11.5,
  lineHeight: 1.45,
  whiteSpace: "pre-wrap" as const,
  wordBreak: "break-word" as const,
  color: COLORS.text,
  background: COLORS.bg,
  border: `1px solid ${COLORS.border}`,
  borderRadius: 1,
  maxHeight: 280,
  overflow: "auto",
  ...scrollSx,
};

const Section: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <Box sx={{ mt: 1 }}>
    <Typography sx={{ fontSize: 10.5, letterSpacing: 0.6, textTransform: "uppercase", color: COLORS.muted }}>
      {label}
    </Typography>
    {children}
  </Box>
);

const Tag: React.FC<{ color: string; children: React.ReactNode }> = ({ color, children }) => (
  <Box
    component="span"
    sx={{
      px: 0.75,
      py: "1px",
      borderRadius: 0.75,
      fontSize: 10.5,
      fontWeight: 600,
      color,
      border: `1px solid ${color}55`,
      whiteSpace: "nowrap",
    }}
  >
    {children}
  </Box>
);

function describeEntry(entry: LiveDebugEntry): { color: string; label: string; title: string; meta: string } {
  if (entry.kind === "turn") {
    const { turn } = entry;
    return {
      color: turn.failed_attempts > 0 ? COLORS.error : COLORS.turn,
      label: `turn ${turn.turn_index}`,
      title: turn.feature || "LLM calls",
      meta: [
        `${turn.attempt_count} API attempts`,
        formatTokens(turn.total_tokens),
        formatCost(turn.total_cost),
      ]
        .filter(Boolean)
        .join(" · "),
    };
  }
  if (entry.kind === "progress") {
    return { color: COLORS.muted, label: "progress", title: entry.text, meta: "" };
  }
  if (entry.kind === "tool") {
    const { row } = entry;
    if (row.audit_event_type === "playbook_context") {
      return { color: COLORS.playbook, label: "playbook", title: row.tool_name, meta: "" };
    }
    const failed = isErrorRow(row);
    const running = !row.end_timestamp;
    return {
      color: failed ? COLORS.error : COLORS.tool,
      label: failed ? "tool · error" : running ? "tool · running" : "tool",
      title: row.tool_name,
      meta: running ? "" : formatDuration(row.start_timestamp, row.end_timestamp),
    };
  }
  const { message } = entry;
  const isTheo = message?.user_id === "neo";
  const contentType = String(message?.content_type || "TEXT");
  const firstLine = String(message?.content || "").trim().split("\n")[0] || `(${contentType.toLowerCase()})`;
  return {
    color: message?.status === "failed" ? COLORS.error : isTheo ? COLORS.theo : COLORS.user,
    label: [isTheo ? "theo" : "you", contentType !== "TEXT" ? contentType.toLowerCase() : "", message?.isStreaming ? "streaming" : ""]
      .filter(Boolean)
      .join(" · "),
    title: firstLine,
    meta: "",
  };
}

const ToolDetail: React.FC<{ row: AuditRow }> = ({ row }) => {
  const params = formatParamsAsKeyValue(row.param_json);
  const output = getFullOutputString(row.output_json);
  return (
    <>
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mt: 0.5 }}>
        <Tag color={COLORS.muted}>{row.status || (row.end_timestamp ? "DONE" : "RUNNING")}</Tag>
        {row.audit_event_type !== "playbook_context" && <Tag color={COLORS.muted}>{getActionTypeLabel(row)}</Tag>}
        {row.user_email && <Tag color={COLORS.muted}>{row.user_email}</Tag>}
        {row.audit_turn_id && <Tag color={COLORS.muted}>turn {String(row.audit_turn_id).slice(0, 8)}</Tag>}
        {row.is_undoable && <Tag color={COLORS.user}>undoable</Tag>}
      </Box>
      {row.description && row.description !== row.reasoning && (
        <Typography sx={{ fontSize: 12.5, color: COLORS.text, mt: 0.75 }}>{row.description}</Typography>
      )}
      {row.reasoning && (
        <Section label="Reasoning">
          <Typography sx={{ fontSize: 12.5, color: COLORS.text, whiteSpace: "pre-wrap", mt: 0.25 }}>{row.reasoning}</Typography>
        </Section>
      )}
      {params && (
        <Section label="Parameters">
          <Box component="pre" sx={preSx}>{params}</Box>
        </Section>
      )}
      {row.error_message && (
        <Section label="Error">
          <Box component="pre" sx={{ ...preSx, color: COLORS.error }}>{row.error_message}</Box>
        </Section>
      )}
      {output && (
        <Section label="Output">
          <Box component="pre" sx={preSx}>{output}</Box>
        </Section>
      )}
      {row.call_id && (
        <Typography sx={{ mt: 0.75, fontSize: 10.5, color: COLORS.muted }}>call {row.call_id}</Typography>
      )}
    </>
  );
};

const Stat: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <Box sx={{ minWidth: 0 }}>
    <Typography sx={{ fontSize: 10, letterSpacing: 0.5, textTransform: "uppercase", color: COLORS.muted }}>{label}</Typography>
    <Typography sx={{ fontSize: 13, fontWeight: 600, color: COLORS.text }}>{value}</Typography>
  </Box>
);

const TurnDetail: React.FC<{ turn: ThreadTurnUsage }> = ({ turn }) => (
  <>
    <Box sx={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 1, mt: 0.5 }}>
      <Stat label="Input" value={formatTokens(turn.input_tokens)} />
      <Stat label="Output" value={formatTokens(turn.output_tokens)} />
      <Stat label="Cost" value={formatCost(turn.total_cost)} />
      <Stat label="API attempts" value={String(turn.attempt_count)} />
      <Stat label="API time" value={`${(turn.duration_ms / 1000).toFixed(1)}s`} />
      <Stat label="Status" value={turn.failed_attempts > 0 ? "Has failed attempts" : "Success"} />
    </Box>
    <Typography sx={{ mt: 0.75, fontSize: 10.5, color: COLORS.muted }}>
      {turn.feature} · turn {turn.turn_id}
    </Typography>
  </>
);

const MessageDetail: React.FC<{ message: any }> = ({ message }) => {
  const review = message?.human_review;
  const reviewTools = review?.tools?.length
    ? review.tools.map((tool: any) => tool?.tool_name).filter(Boolean)
    : ([] as string[]).concat(review?.tool_name ?? []);
  return (
    <>
      {message?.content ? (
        <Box component="pre" sx={preSx}>{message.content}</Box>
      ) : null}
      {reviewTools.length > 0 && (
        <Section label="Awaiting approval for">
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mt: 0.5 }}>
            {reviewTools.map((name: string, index: number) => (
              <Tag key={`${name}-${index}`} color={COLORS.tool}>{name}</Tag>
            ))}
          </Box>
        </Section>
      )}
      {message?.error && (
        <Section label="Error">
          <Box component="pre" sx={{ ...preSx, color: COLORS.error }}>{message.error}</Box>
        </Section>
      )}
      <Typography sx={{ mt: 0.75, fontSize: 10.5, color: COLORS.muted }}>
        {[message?.message_id && `id ${message.message_id}`, message?.agent_display_name].filter(Boolean).join(" · ")}
      </Typography>
    </>
  );
};

export interface LiveDebugPanelProps {
  aiThreadId: string | number | null | undefined;
  messages: any[];
  isThinking?: boolean;
  onClose: () => void;
}

const LiveDebugPanel: React.FC<LiveDebugPanelProps> = ({ aiThreadId, messages, isThinking, onClose }) => {
  const threadId = aiThreadId != null && aiThreadId !== "" ? String(aiThreadId) : null;
  const [auditRows, setAuditRows] = useState<AuditRow[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [overrides, setOverrides] = useState<Record<string, boolean>>({});
  const [slot, setSlot] = useState<HTMLElement | null>(() => document.getElementById(LIVE_DEBUG_SLOT_ID));
  const requestSeq = useRef(0);
  const requestThread = useRef<string | null>(threadId);
  const [usage, setUsage] = useState<ThreadUsage | null>(null);
  // Root-only routes answer 403/404 for everyone else; stop asking once they have said no.
  const [usageBlocked, setUsageBlocked] = useState(false);
  const [traceUrl, setTraceUrl] = useState<string>("");
  const [automation, setAutomation] = useState<ThreadAutomation | null>(null);
  const [resolver, setResolver] = useState<ThreadResolverState | null>(null);
  const [progress, setProgress] = useState<ProgressUpdate[]>([]);
  const seenStepCount = useRef(0);
  const thinkingSteps = useSelector((state: any) => state.chat?.thinkingSteps) as unknown[] | undefined;
  const listRef = useRef<HTMLDivElement | null>(null);
  const stickToBottom = useRef(true);

  useEffect(() => {
    if (!slot) setSlot(document.getElementById(LIVE_DEBUG_SLOT_ID));
  }, [slot]);

  const fetchAudit = useCallback(async () => {
    if (!threadId) return;
    const seq = ++requestSeq.current;
    setLoading(true);
    try {
      chatApi.invalidateThreadAuditCache(threadId);
      const data = await chatApi.getToolAuditLogs(threadId);
      if (seq !== requestSeq.current) return;
      setAuditRows(Array.isArray(data) ? data : []);
      setError(null);
    } catch (err: any) {
      if (seq !== requestSeq.current) return;
      setError(err?.response?.data?.error ?? err?.message ?? "Failed to load audit logs");
    } finally {
      if (seq === requestSeq.current) setLoading(false);
    }
  }, [threadId]);

  /** Data that only changes between turns: Usage totals, trace link, mode and resolver state. */
  const fetchTurnData = useCallback(async () => {
    if (!threadId) return;
    const forThread = threadId;
    const [automationResult, resolverResult] = await Promise.allSettled([
      chatApi.getThreadAutomation(forThread),
      chatApi.getThreadResolverState(forThread),
    ]);
    if (requestThread.current !== forThread) return;
    if (automationResult.status === "fulfilled") setAutomation(automationResult.value);
    if (resolverResult.status === "fulfilled") setResolver(resolverResult.value);
    if (usageBlocked) return;
    const [usageResult, traceResult] = await Promise.allSettled([
      getThreadUsage(Number(forThread)),
      chatApi.getLinearTaskTraceUrl(forThread),
    ]);
    if (requestThread.current !== forThread) return;
    const denied = (result: PromiseSettledResult<unknown>) =>
      result.status === "rejected" && [401, 403, 404].includes((result.reason as any)?.response?.status);
    if (denied(usageResult) && denied(traceResult)) {
      setUsageBlocked(true);
      return;
    }
    if (usageResult.status === "fulfilled") setUsage(usageResult.value);
    if (traceResult.status === "fulfilled") setTraceUrl(String(traceResult.value?.trace_url || ""));
  }, [threadId, usageBlocked]);

  // New thread: start clean.
  useEffect(() => {
    requestSeq.current += 1;
    requestThread.current = threadId;
    setAuditRows([]);
    setOverrides({});
    setError(null);
    setUsage(null);
    setTraceUrl("");
    setAutomation(null);
    setResolver(null);
    setProgress([]);
  }, [threadId]);

  // Keep Cortex's progress updates. Redux clears them when the turn ends; the panel keeps a copy.
  useEffect(() => {
    const steps = Array.isArray(thinkingSteps) ? thinkingSteps : [];
    if (steps.length < seenStepCount.current) seenStepCount.current = 0;
    const fresh = steps.slice(seenStepCount.current);
    seenStepCount.current = steps.length;
    if (!threadId || fresh.length === 0) return;
    const now = Date.now();
    setProgress((prev) => {
      const next = [...prev];
      fresh.forEach((step, index) => {
        const text = typeof step === "string" ? step : JSON.stringify(step);
        if (!text.trim() || next[next.length - 1]?.text === text) return;
        next.push({ key: `progress-${now}-${index}`, at: now, text });
      });
      return next;
    });
  }, [thinkingSteps, threadId]);

  // Turn-level data: on open, when a turn ends, and again once LangSmith has caught up.
  useEffect(() => {
    if (!threadId || isThinking) return;
    void fetchTurnData();
    const settle = window.setTimeout(() => void fetchTurnData(), LANGSMITH_SETTLE_MS);
    return () => window.clearTimeout(settle);
  }, [threadId, isThinking, fetchTurnData]);

  // Refetch whenever the conversation moves, and keep polling: fast while Theo works, slow otherwise.
  const messageCount = messages.length;
  useEffect(() => {
    if (!threadId) return;
    void fetchAudit();
    const timer = window.setInterval(() => void fetchAudit(), isThinking ? POLL_WHILE_THINKING_MS : POLL_IDLE_MS);
    return () => window.clearInterval(timer);
  }, [threadId, isThinking, messageCount, fetchAudit]);

  const timeline = useMemo(
    () => buildLiveDebugTimeline(messages, auditRows, usage?.turns ?? [], progress),
    [messages, auditRows, usage, progress]
  );
  // Only messages and tool calls auto-open; turn markers and progress lines stay folded.
  const latestKey = useMemo(() => {
    for (let i = timeline.length - 1; i >= 0; i -= 1) {
      if (timeline[i].kind === "message" || timeline[i].kind === "tool") return timeline[i].key;
    }
    return undefined;
  }, [timeline]);

  // Follow new entries like a chat, unless the user scrolled up to read something.
  useEffect(() => {
    const list = listRef.current;
    if (list && stickToBottom.current) list.scrollTop = list.scrollHeight;
  }, [timeline]);
  const isOpen = (key: string) => overrides[key] ?? key === latestKey;
  const toggle = (key: string) => setOverrides((prev) => ({ ...prev, [key]: !isOpen(key) }));

  const toolCount = auditRows.length;
  const errorCount = auditRows.filter((row) => isErrorRow(row)).length;

  const panel = (
    <Box
      className="theo-live-debug-panel"
      sx={{
        width: PANEL_WIDTH,
        flexShrink: 0,
        height: "100%",
        display: "flex",
        flexDirection: "column",
        background: COLORS.bg,
        color: COLORS.text,
        borderLeft: `1px solid ${COLORS.border}`,
        fontFamily: "var(--theo-font-sans)",
        ...(slot
          ? {}
          : { position: "fixed", top: 0, right: 0, bottom: 0, height: "100vh", zIndex: 1300, boxShadow: "-8px 0 24px rgba(0,0,0,0.25)" }),
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, px: 1.5, py: 1, borderBottom: `1px solid ${COLORS.border}` }}>
        <Box
          sx={{
            width: 8,
            height: 8,
            borderRadius: "50%",
            background: isThinking ? COLORS.user : COLORS.muted,
            boxShadow: isThinking ? `0 0 6px ${COLORS.user}` : "none",
          }}
        />
        <Typography sx={{ fontWeight: 600, fontSize: 13.5 }}>Live debug</Typography>
        <Typography sx={{ fontSize: 11.5, color: COLORS.muted, flex: 1, minWidth: 0 }} noWrap>
          {threadId ? `thread ${threadId} · ${toolCount} tool${toolCount === 1 ? "" : "s"}` : "no thread"}
          {errorCount > 0 ? ` · ${errorCount} error${errorCount === 1 ? "" : "s"}` : ""}
        </Typography>
        <Tooltip title="Refresh">
          <span>
            <IconButton
              size="small"
              onClick={() => {
                void fetchAudit();
                void fetchTurnData();
              }}
              disabled={!threadId || loading}
              sx={{ color: COLORS.muted }}
            >
              <RefreshIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </span>
        </Tooltip>
        {traceUrl && (
          <Tooltip title="Open latest turn in LangSmith">
            <IconButton size="small" component="a" href={traceUrl} target="_blank" rel="noopener noreferrer" sx={{ color: COLORS.muted }}>
              <OpenInNewIcon sx={{ fontSize: 17 }} />
            </IconButton>
          </Tooltip>
        )}
        <Tooltip title="Turn off live debug">
          <IconButton size="small" onClick={onClose} sx={{ color: COLORS.muted }}>
            <CloseIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Tooltip>
      </Box>
      {(usage || automation || resolver?.resolver_state) && (
        <Box sx={{ px: 1.5, py: 1, borderBottom: `1px solid ${COLORS.border}` }}>
          {usage && (
            <Box sx={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 1 }}>
              <Stat label="Tokens in" value={formatTokens(usage.input_tokens)} />
              <Stat label="Tokens out" value={formatTokens(usage.output_tokens)} />
              <Stat label="Cost" value={formatCost(usage.total_cost)} />
              <Stat label="Turns · API attempts" value={`${usage.turns.length} · ${usage.attempt_count}`} />
            </Box>
          )}
          {(automation || resolver?.resolver_state) && (
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mt: usage ? 1 : 0 }}>
              {automation && (
                <>
                  <Tag color={COLORS.theo}>{AUTOMATION_LABEL[automation.automation_capacity] ?? `mode ${automation.automation_capacity}`}</Tag>
                  {automation.automation_capacity !== 0 && (
                    <Tag color={COLORS.muted}>{INTERACTION_LABEL[automation.customer_interaction] ?? `interaction ${automation.customer_interaction}`}</Tag>
                  )}
                  {automation.agent_id ? <Tag color={COLORS.muted}>agent {automation.agent_id}</Tag> : null}
                </>
              )}
              {resolver?.resolver_state && (
                <Tag color={COLORS.tool}>
                  resolver {resolver.resolver_state}
                  {resolver.resolver_context?.on ? ` · on ${resolver.resolver_context.on}` : ""}
                  {resolver.resolver_context?.kind ? ` · ${resolver.resolver_context.kind}` : ""}
                </Tag>
              )}
            </Box>
          )}
          {resolver?.resolver_context?.reason && (
            <Typography sx={{ mt: 0.5, fontSize: 11.5, color: COLORS.muted }}>{resolver.resolver_context.reason}</Typography>
          )}
        </Box>
      )}
      {error && (
        <Typography sx={{ px: 1.5, py: 0.75, fontSize: 12, color: COLORS.error, borderBottom: `1px solid ${COLORS.border}` }}>
          {error}
        </Typography>
      )}
      <Box
        ref={listRef}
        onScroll={(event) => {
          const list = event.currentTarget;
          stickToBottom.current = list.scrollHeight - list.scrollTop - list.clientHeight < 48;
        }}
        sx={{ flex: 1, minHeight: 0, overflow: "auto", p: 1, display: "flex", flexDirection: "column", gap: 0.75, ...scrollSx }}>
        {timeline.length === 0 && (
          <Typography sx={{ fontSize: 12.5, color: COLORS.muted, p: 1 }}>
            {threadId ? "Nothing yet. Messages and tool calls show up here as they happen." : "Open a chat to start."}
          </Typography>
        )}
        {timeline.map((entry) => {
          if (entry.kind === "progress") {
            return (
              <Box key={entry.key} sx={{ display: "flex", gap: 1, px: 1, flexShrink: 0, alignItems: "baseline" }}>
                <Typography sx={{ fontSize: 11.5, color: COLORS.muted, flex: 1, minWidth: 0, fontStyle: "italic" }} noWrap title={entry.text}>
                  › {entry.text}
                </Typography>
                <Typography sx={{ fontSize: 10.5, color: COLORS.muted, whiteSpace: "nowrap" }}>{formatClock(entry.at)}</Typography>
              </Box>
            );
          }
          const open = isOpen(entry.key);
          const { color, label, title, meta } = describeEntry(entry);
          return (
            <Box
              key={entry.key}
              sx={{
                background: COLORS.card,
                border: `1px solid ${open ? `${color}66` : COLORS.border}`,
                borderLeft: `3px solid ${color}`,
                borderRadius: 1,
                flexShrink: 0,
              }}
            >
              <Box
                role="button"
                tabIndex={0}
                aria-expanded={open}
                onClick={() => toggle(entry.key)}
                onKeyDown={(event) => {
                  if (event.key === "Enter" || event.key === " ") {
                    event.preventDefault();
                    toggle(entry.key);
                  }
                }}
                sx={{ display: "flex", alignItems: "center", gap: 1, px: 1, py: 0.75, cursor: "pointer", userSelect: "none" }}
              >
                <Typography sx={{ fontSize: 10.5, color, fontWeight: 600, whiteSpace: "nowrap", textTransform: "uppercase" }}>
                  {label}
                </Typography>
                <Typography
                  sx={{
                    flex: 1,
                    minWidth: 0,
                    fontSize: 12.5,
                    color: COLORS.text,
                    fontFamily: entry.kind === "tool" ? "ui-monospace, SFMono-Regular, Menlo, monospace" : "inherit",
                  }}
                  noWrap
                >
                  {title}
                </Typography>
                {meta && <Typography sx={{ fontSize: 11, color: COLORS.muted }}>{meta}</Typography>}
                <Typography sx={{ fontSize: 11, color: COLORS.muted, whiteSpace: "nowrap" }}>{formatClock(entry.at)}</Typography>
              </Box>
              {open && (
                <Box sx={{ px: 1, pb: 1 }}>
                  {entry.kind === "tool" ? (
                    <ToolDetail row={entry.row} />
                  ) : entry.kind === "turn" ? (
                    <TurnDetail turn={entry.turn} />
                  ) : (
                    <MessageDetail message={entry.message} />
                  )}
                </Box>
              )}
            </Box>
          );
        })}
      </Box>
    </Box>
  );

  return createPortal(panel, slot ?? document.body);
};

export default LiveDebugPanel;
